/**
 * Critical effective inventory: low weeks of supply using shelf stock (+ in-transit on slips only),
 * excluding PO quantities not yet on a packing slip. Used to flag supplier follow-up (e.g. Jiahong).
 */

export const CRITICAL_WEEKS_THRESHOLD = 4;

export function buildInTransitByPartId(purchaseLogs, packingSlips) {
    const purchasedPartToPartId = {};
    (purchaseLogs || []).forEach(log => {
        if (log.partId && log.id) {
            purchasedPartToPartId[log.id] = log.partId;
        }
    });

    const inTransitByPartId = {};
    (packingSlips || []).forEach(slip => {
        (slip.items || []).forEach(item => {
            const purchasedPartId = item.purchasedPartId || item.purchasedPart?.id;
            const partId = purchasedPartToPartId[purchasedPartId];
            if (!partId) return;
            const remaining = (item.quantityExpected || 0) - (item.quantityReceived || 0);
            if (remaining > 0) {
                inTransitByPartId[partId] = (inTransitByPartId[partId] || 0) + remaining;
            }
        });
    });
    return inTransitByPartId;
}

export function getWeeksHighlightColor(weeks) {
    if (weeks === "∞" || weeks === Infinity || weeks === null || weeks === undefined) {
        return 'white';
    }
    const n = Number(weeks);
    if (Number.isNaN(n)) return 'white';
    if (n < 1) return 'red';
    if (n < 3) return 'orange';
    if (n < 5) return 'yellow';
    return 'white';
}

export function isCriticalWeeksValue(weeks) {
    if (weeks === "∞" || weeks === Infinity) return false;
    const n = Number(weeks);
    return !Number.isNaN(n) && n <= CRITICAL_WEEKS_THRESHOLD;
}

/**
 * Weeks until critical inventory (effective + in-transit on slips) runs out at average sales.
 */
export function computeCriticalEffectiveInventoryWeeks({
    inTransit,
    minReorderWeeksResult,
    reorderByMinimumQuantityBelowThreshold = false,
    combinedEffectiveInventory,
    effectiveInventory,
    minQuantity,
    averageSalesPerWeek,
    combinedAverageSalesPerWeek,
    leadTimeInWeeks,
    weeksToOrderBuffer,
    partExpanded,
    part,
    aggregatedSupersedesData,
    parts,
    hasSupersedesChainWithSales,
    inTransitByPartId,
}) {
    const transit = Number(inTransit) || 0;
    const onHand = Number(effectiveInventory) || 0;
    const criticalInventoryQuantity = onHand + transit;

    const belowMinThreshold = reorderByMinimumQuantityBelowThreshold
        || (minReorderWeeksResult && minReorderWeeksResult.belowMinThreshold);

    const salesPerWeek = aggregatedSupersedesData ? combinedAverageSalesPerWeek : averageSalesPerWeek;

    let criticalInventoryWeeks = "∞";
    if (
        salesPerWeek > 0 &&
        (partExpanded.fourWeekAvgSales > 0 || partExpanded.twentyFourWeekAvgSales > 0)
    ) {
        const weeksUntilOut = Math.floor(criticalInventoryQuantity / salesPerWeek);
        if (!Number.isNaN(weeksUntilOut)) {
            criticalInventoryWeeks = weeksUntilOut;
        }
    }

    const isCritical = isCriticalWeeksValue(criticalInventoryWeeks) || !!belowMinThreshold;
    const colorWeeks = belowMinThreshold ? 0 : criticalInventoryWeeks;

    return {
        criticalInventoryQuantity,
        criticalInventoryWeeks,
        criticalWeeksToOrder: criticalInventoryWeeks,
        isCritical,
        color: getWeeksHighlightColor(colorWeeks),
    };
}
